import { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import moment from "moment"
import { useCalendarJournalNoteDates } from "../../../hooks/useCalendarSelectors"
import { useTimeOffForDate } from "../../../hooks/useTimeOff"
import { useRefreshCalendarBookings } from "../../../useRefreshCalendarBookings"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import { computeEventColumns, getLanesForDay, getDayMinWidth, HOUR_HEIGHT } from "../timeGridUtils"
import { UNASSIGNED_ID } from "../../../constants"

const getResourceLabel = (resource) => {
  if (resource === "unassigned_equipment" || resource === "unassigned_crew") return "Unassigned"
  return typeof resource === "string" ? resource : resource?.title || ""
}

const getResourceEvents = (dayEvents, resource, isEquipmentTab, isCrewTab, resourceId) => {
  if (isEquipmentTab) {
    if (resource === "unassigned_equipment") return dayEvents.filter((ev) => !ev.assigned_equipment || ev.assigned_equipment.length === 0)
    return dayEvents.filter((ev) =>
      (ev.assigned_equipment || []).length > 0 &&
      (ev.assigned_equipment || []).some((item) => item?.name === resource || item?.equipment_title === resource)
    )
  }
  if (isCrewTab) {
    if (resource === "unassigned_crew" || resource === "Unassigned") return dayEvents.filter((ev) => !ev.assigned_crew || ev.assigned_crew.length === 0)
    return dayEvents.filter((ev) => (ev.assigned_crew || []).some((item) => item?.name === resource || item?.full_name === resource))
  }
  if (resourceId) return dayEvents.filter((ev) => ev.experience_id === resourceId)
  return dayEvents.filter((ev) => ev.resource === getResourceLabel(resource))
}

const useDayGridData = ({ date, hours, events, resources: resourcesProp, profileIds, boatIds, activeTab, startHour, endHour }) => {
  const calendarCrew = useSelector((state) => state.staff?.calendarCrew || [])
  const equipmentList = useSelector((state) =>
    (state.calendar?.calendarEquipment || []).map((eq) => ({ id: eq.id, name: eq.equipment_title }))
  )
  const isCrewTab = activeTab === "crew"
  const isEquipmentTab = activeTab === "equipment"

  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false)
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)

  const journalNoteDates = useCalendarJournalNoteDates()
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()
  const timeOffForDay = useTimeOffForDate(date)
  const refreshCalendarBookings = useRefreshCalendarBookings()

  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)
  const currentDay = moment(date).format("YYYY-MM-DD")
  const hasJournalNote = journalNoteDates?.includes(currentDay)
  const hasJournalPermission = hasJournalNote ? canViewJournal || canEditJournal : canCreateJournal

  /**
   * Row label -> the record behind it, so a row can show the crew member's role and be matched to
   * their blackout by id. The grids address rows by name (see `getResourceEvents`), which carries
   * neither.
   */
  const crewByName = useMemo(
    () => new Map(calendarCrew.map((crew) => [crew.name, crew])),
    [calendarCrew]
  )

  const equipmentByName = useMemo(
    () => new Map(equipmentList.map((equipment) => [equipment.name, equipment])),
    [equipmentList]
  )

  const crewMembers = useMemo(() => {
    if (!isCrewTab) return []
    const active = calendarCrew.filter((c) => c.status?.toLowerCase() === "active")
    if (profileIds?.length > 0) {
      const ids = profileIds.filter((id) => id !== UNASSIGNED_ID && id !== "unassigned").map(String)
      return ids.length > 0 ? active.filter((c) => ids.includes(String(c.id))) : active
    }
    return active
  }, [calendarCrew, profileIds, isCrewTab])

  const normalizedDayEvents = useMemo(() => events.map((ev) => {
    const startMoment = ev.start ? moment(ev.start, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
    const endMoment = ev.end ? moment(ev.end, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
    return { ...ev, resource: ev.resource || ev.title || "Experience", startMoment, endMoment, startHour: startMoment?.isValid() ? startMoment.hour() : null, endHour: endMoment?.isValid() ? endMoment.hour() : null, startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start, endLabel: endMoment?.isValid() ? endMoment.format("h:mm A") : ev.end }
  }), [events])

  const dayEvents = useMemo(() => normalizedDayEvents.filter((ev) => {
    if (moment(ev.date).format("YYYY-MM-DD") !== currentDay) return false
    if (ev.startHour === null) return true
    return ev.startHour >= startHour && ev.startHour < endHour
  }), [normalizedDayEvents, currentDay, startHour, endHour])

  const resources = useMemo(() => {
    if (isCrewTab) {
      // If filters are applied, respect them exactly
      if (profileIds?.length > 0) {
        const rows = []
        if (profileIds.includes(UNASSIGNED_ID) || profileIds.includes("unassigned")) {
          rows.push("unassigned_crew")
        }
        const selectedCrewIds = new Set(
          profileIds.filter((id) => id !== UNASSIGNED_ID && id !== "unassigned").map(String)
        )
        const selectedCrew = crewMembers.filter((c) => selectedCrewIds.has(String(c.id)))
        rows.push(...selectedCrew.map((c) => c.name))
        return rows
      }

      // Default: show all crew + unassigned
      return ["unassigned_crew", ...crewMembers.map((c) => c.name)]
    }
    if (isEquipmentTab) {
      // If filters are applied, respect them exactly
      if (boatIds?.length > 0) {
        const rows = []
        if (boatIds.includes(UNASSIGNED_ID) || boatIds.includes("unassigned")) {
          rows.push("unassigned_equipment")
        }
        const selectedBoatIds = new Set(
          boatIds.filter((id) => id !== UNASSIGNED_ID && id !== "unassigned").map(String)
        )
        const selectedEquipment = equipmentList.filter((eq) => selectedBoatIds.has(String(eq.id)))
        rows.push(...selectedEquipment.map((eq) => eq.name))
        return rows
      }

      // Default: show all equipment + unassigned
      return ["unassigned_equipment", ...equipmentList.map((eq) => eq.name)]
    }
    const explicit = (resourcesProp || []).filter(Boolean)
    if (explicit.length) {
      const seen = new Set()
      return explicit.filter((item) => {
        const key = item?.id ?? item
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    }
    const names = normalizedDayEvents.map((ev) => ev.resource).filter(Boolean)
    const unique = Array.from(new Set(names))
    return unique.length ? unique : ["Experience"]
  }, [normalizedDayEvents, resourcesProp, isCrewTab, isEquipmentTab, equipmentList, crewMembers, boatIds, profileIds, dayEvents])

  const totalHeight = hours.length * HOUR_HEIGHT

  const { gridTemplateColumns, gridMinWidth } = useMemo(() => {
    const colData = resources.map((resource) => {
      const resourceId = typeof resource === "string" ? null : resource?.id
      const resEvents = getResourceEvents(dayEvents, resource, isEquipmentTab, isCrewTab, resourceId)
      const valid = resEvents.filter((ev) => ev.startMoment?.isValid() && ev.endMoment?.isValid())
      const lanes = getLanesForDay(computeEventColumns(valid))
      return getDayMinWidth(lanes)
    })
    const cols = colData.map((w) => `minmax(${w}px, 1fr)`)
    return {
      gridTemplateColumns: `52px 1px ${cols.join(" ")}`,
      gridMinWidth: 52 + 1 + colData.reduce((a, w) => a + w, 0),
    }
  }, [resources, dayEvents, isEquipmentTab, isCrewTab])

  const handleBookingCancel = () => { setIsBookingModalOpen(false); setSelectedBookingId(null) }
  const handleBookingCancelSuccess = () => { handleBookingCancel(); refreshCalendarBookings() }

  return {
    isJournalModalOpen, setIsJournalModalOpen, isBookingModalOpen, setIsBookingModalOpen,
    selectedBookingId, setSelectedBookingId, currentDay, hasJournalPermission, journalNoteDates,
    active, timeOffForDay, dayEvents, resources, totalHeight, gridTemplateColumns, gridMinWidth,
    isCrewTab, isEquipmentTab, handleBookingCancel, handleBookingCancelSuccess, crewByName, equipmentByName,
    refreshCalendarBookings,
    getResourceLabel, getResourceEvents,
  }
}

export { getResourceLabel, getResourceEvents }
export default useDayGridData
